import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import {
  MAX_ORIENTATION_POINTS,
  sagittalFromLandmarks,
  scoreApt,
} from './hip-displacement-horizontal-strategy';
import type { OrientationScore } from './hip-displacement-horizontal-strategy';
import { logPostureClassificationDebug } from './posture-classification-debug';
import type { Landmark } from '@/components/posture-analysis/types';
import { IDX } from '@/components/posture-analysis/constants';

const STRATEGY_ID = 'adaptiveThresholdVprOverrideV2';
const STRATEGY_NAME = '20260711 Adaptive Threshold VPR Override v2 (knee-flex tibial guard + Right-view ASIS/PSIS)';
const DEBUG_LOG_PREFIX = '[adaptiveThresholdVprOverrideV2]';

const MAX_AUTO_POINTS = 7;

/**
 * Adaptive thresholds — distinct from the parent strategy's hard-coded values.
 *
 * Key differences from `indicatorPointScoreVprOverride`:
 *
 * 1. **Softened C-curve veto**: Hard APT exclusion requires torso offset < -5.0 cm
 *    (was -2.5 cm). The range -2.5 cm to -5.0 cm applies a weighted penalty
 *    (-2 APT points) instead of a binary exclusion. This fixes Salvador (-2.7 cm)
 *    who was incorrectly excluded from APT by a razor-thin margin.
 *
 * 2. **Borderline pelvic-tilt guard**: A universal "mild anterior tilt" zone
 *    (≥ 3°, sex-independent) that partially suppresses knee-flexion Swayback
 *    signals (+1 instead of +2). This fixes Conny (pelvic tilt 4.0°, below the
 *    5° male threshold but structurally anterior). Intentionally not sex-resolved
 *    because this is a *low-bar borderline guard*, not a classification threshold.
 *
 * 3. **Bidirectional FHP mapping**: When forward head posture is present AND
 *    pelvic tilt is anterior (≥ mildAnteriorTiltDeg), the FHP is interpreted as
 *    a compensatory lordotic APT indicator rather than a Swayback signal.
 *    This fixes Conny (22.4° FHP with anterior pelvic tilt).
 */

// --- Adaptive-specific thresholds ---
// Mild anterior tilt zone — intentionally sex-independent at 3° because this is
// a *borderline guard* to detect any hint of anterior orientation, not a full
// classification threshold. The full APT threshold is sex-resolved elsewhere
// (5° male / 7.5° female via evaluation-rules.yaml).
const MILD_ANTERIOR_TILT_DEG = 3.0;

function resolveMildAnteriorTiltDeg(_clientSex: 'Male' | 'Female' | 'Unknown'): number {
  // Sex-independent: the mild zone uses a universal 3° threshold.
  // Sex resolution happens at the full pelvic tilt threshold (pelvicTiltDegMin).
  return MILD_ANTERIOR_TILT_DEG;
}
const HARD_C_CURVE_VETO_CM = -5.0;
const SOFT_C_CURVE_PENALTY_CM = -2.5;
const C_CURVE_PENALTY_POINTS = 2;

interface VetoThresholds {
  slrSwaybackVetoMaxDeg: number;
  kneeFlexionExtremeDegMin: number;
  fullHipExtensionDeg: number;
  oberNeutralDeg: number;
  kneeHyperextensionExtremeDegMin: number;
  pelvicTiltVetoDegMin: number;
  overridePointBoost: number;
}

const DEFAULT_VETO_THRESHOLDS: VetoThresholds = {
  slrSwaybackVetoMaxDeg: 45,
  kneeFlexionExtremeDegMin: 10,
  fullHipExtensionDeg: 0,
  oberNeutralDeg: 0,
  kneeHyperextensionExtremeDegMin: 5.0,
  pelvicTiltVetoDegMin: 15.0,
  overridePointBoost: 3,
};

function readVetoThresholds(rules: any): VetoThresholds {
  const v = rules?.vetoExclusion ?? {};
  return {
    slrSwaybackVetoMaxDeg: v.slrSwaybackVetoMaxDeg ?? DEFAULT_VETO_THRESHOLDS.slrSwaybackVetoMaxDeg,
    kneeFlexionExtremeDegMin:
      v.kneeFlexionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeFlexionExtremeDegMin,
    fullHipExtensionDeg: v.fullHipExtensionDeg ?? DEFAULT_VETO_THRESHOLDS.fullHipExtensionDeg,
    oberNeutralDeg: v.oberNeutralDeg ?? DEFAULT_VETO_THRESHOLDS.oberNeutralDeg,
    kneeHyperextensionExtremeDegMin:
      v.kneeHyperextensionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeHyperextensionExtremeDegMin,
    pelvicTiltVetoDegMin: v.pelvicTiltVetoDegMin ?? DEFAULT_VETO_THRESHOLDS.pelvicTiltVetoDegMin,
    overridePointBoost: v.overridePointBoost ?? DEFAULT_VETO_THRESHOLDS.overridePointBoost,
  };
}

const sumDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) : null;
};

const avgDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

const bothAtLeast = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a >= threshold &&
  b >= threshold;

const bothAtMost = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a <= threshold &&
  b <= threshold;

const rad2deg = (r: number) => (r * 180) / Math.PI;

function angleAtLandmark(a: Landmark, b: Landmark, c: Landmark): number {
  if (!a || !b || !c) return NaN;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mab = Math.hypot(ab.x, ab.y);
  const mcb = Math.hypot(cb.x, cb.y);
  if (mab === 0 || mcb === 0) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
  return rad2deg(Math.acos(cos));
}

interface AugmentedScore {
  pts: number;
  evidence: string[];
  activeSignals: string[];
}

/**
 * Adaptive Swayback scoring — differs from base `scoreSwayback()` by using a
 * three-tier pelvic tilt guard instead of the binary full-threshold-only guard.
 *
 * Tiers:
 * - pelvicTilt ≥ pelvicTiltDegMin (full threshold): FULL suppression of knee-flexion (+0)
 * - pelvicTilt ≥ mildAnteriorTiltDeg (sex-resolved): PARTIAL suppression (+1 instead of +2)
 * - pelvicTilt < mildAnteriorTiltDeg: NO suppression (+2 as normal)
 */
function scoreSwaybackAdaptive(
  hipFwdCm: number,
  torsoOffsetCm: number,
  kneeFlex: boolean,
  pelvicTilt: number,
  rules: any,
  kneeFlexEvidenceDeg?: number,
  kneeHipAntCm?: number,
  pelvicTiltDegMin?: number,
  mildAnteriorTiltDeg: number = MILD_ANTERIOR_TILT_DEG
): OrientationScore {
  const ev: string[] = [];
  const activeSignals: string[] = [];
  let pts = 0;
  const { hipForwardCmMin, strictHip, trunkLeanLatDegMin, strictLean, strictTilt } = rules;

  // Hip forward shift (unchanged)
  if (hipFwdCm > hipForwardCmMin) {
    pts += 2;
    activeSignals.push('sb.hipFwd.markedAnterior');
    ev.push(`[+2] Hip markedly anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm > ${hipForwardCmMin} cm)`);
  } else if (hipFwdCm > strictHip) {
    pts += 1;
    activeSignals.push('sb.hipFwd.mildAnterior');
    ev.push(`[+1] Hip anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm > ${strictHip} cm)`);
  }

  // Torso offset / C-curve (unchanged from base scoring — veto changes are in the veto layer)
  if (torsoOffsetCm < -trunkLeanLatDegMin) {
    pts += 2;
    activeSignals.push('sb.torso.posteriorShouldersStrong');
    ev.push(`[+2] Shoulders posterior to hips vs plumb ("C" curve), Δ ≈ ${torsoOffsetCm.toFixed(1)} cm`);
  } else if (torsoOffsetCm < -strictLean) {
    pts += 1;
    activeSignals.push('sb.torso.posteriorShouldersMild');
    ev.push(`[+1] Mild posterior shoulder shift vs hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm)`);
  }

  // Posterior pelvic tilt (unchanged)
  if (isFinite(pelvicTilt) && pelvicTilt <= -strictTilt) {
    pts += 1;
    activeSignals.push('sb.pelvis.posteriorTilt');
    ev.push(`[+1] Pelvic line suggests posterior tilt (~${pelvicTilt.toFixed(1)}°)`);
  }

  // --- IMPROVEMENT 2: Three-tier knee-flexion guard ---
  const pelvicTiltIsFullAnterior =
    typeof pelvicTiltDegMin === 'number' &&
    isFinite(pelvicTilt) &&
    pelvicTilt >= pelvicTiltDegMin;

  const pelvicTiltIsMildAnterior =
    !pelvicTiltIsFullAnterior &&
    isFinite(pelvicTilt) &&
    pelvicTilt >= mildAnteriorTiltDeg;

  const degStr =
    typeof kneeFlexEvidenceDeg === 'number' && isFinite(kneeFlexEvidenceDeg)
      ? kneeFlexEvidenceDeg.toFixed(0)
      : null;

  if (kneeFlex && pelvicTiltIsFullAnterior) {
    // Full suppression (pelvic tilt ≥ sex-resolved threshold)
    activeSignals.push('sb.knee.flexion.suppressedByAnteriorPelvicTilt');
    ev.push(
      degStr
        ? `[+0] Knee flexion (~${degStr}°) suppressed as Swayback signal: pelvic tilt is anterior (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°) → likely compensatory APT, not primary swayback`
        : `[+0] Knee flexion suppressed as Swayback signal: pelvic tilt is anterior (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°) → likely compensatory APT, not primary swayback`
    );
  } else if (kneeFlex && pelvicTiltIsMildAnterior) {
    // Partial suppression (≥ 3° but below full threshold)
    pts += 1;
    activeSignals.push('sb.knee.flexion.partiallySuppressedByMildAnteriorTilt');
    ev.push(
      degStr
        ? `[+1] Knee flexion (~${degStr}°) partially suppressed: pelvic tilt is mildly anterior (~${pelvicTilt.toFixed(1)}° ≥ ${mildAnteriorTiltDeg}° but < ${pelvicTiltDegMin}°) → reduced from +2 to +1`
        : `[+1] Knee flexion partially suppressed: pelvic tilt is mildly anterior (~${pelvicTilt.toFixed(1)}° ≥ ${mildAnteriorTiltDeg}° but < ${pelvicTiltDegMin}°) → reduced from +2 to +1`
    );
  } else if (kneeFlex) {
    // No suppression
    pts += 2;
    activeSignals.push('sb.knee.flexion');
    ev.push(
      degStr
        ? `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}° (~${degStr}°; typical swayback compensation vs locked knees)`
        : `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}° (typical swayback compensation vs locked knees)`
    );
  }

  // Knee-hip anterior offset (also guarded by pelvic tilt)
  const kneeHipSbMin = rules.kneeHipAnteriorSwaybackCmMin;
  if (
    typeof kneeHipSbMin === 'number' &&
    typeof kneeHipAntCm === 'number' &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm > kneeHipSbMin &&
    !pelvicTiltIsFullAnterior &&
    !pelvicTiltIsMildAnterior
  ) {
    pts += 1;
    activeSignals.push('sb.kneeHip.anterior');
    ev.push(`[+1] Knees anterior to hips (${kneeHipAntCm.toFixed(1)} cm > ${kneeHipSbMin} cm; flexed-knee braking)`);
  }

  return { pts: Math.min(MAX_ORIENTATION_POINTS, pts), evidence: ev, activeSignals };
}

function augmentScores(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext
): {
  sb: AugmentedScore;
  apt: AugmentedScore;
  dynamicMax: number;
  parsed: any;
  fromLm: any;
  rules: any;
} | null {
  const rules = (getEvaluationRules() as any).posture.thresholds;
  const kneeThresholds = {
    kneeFlexionDegMin: rules.kneeFlexionDegMin,
    kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
  };
  const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
  if (!parsed) return null;

  const avgCvAngle = parsed.avgCvAngle;
  const tibialInclination = parsed.tibialInclination;
  const pelvicTiltDegMin =
    input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;

  const fromLm = sagittalFromLandmarks(
    input,
    context,
    kneeThresholds.kneeFlexionDegMin,
    kneeThresholds.kneeHyperextensionDegMin
  );
  const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
  const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
  const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
  const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
  const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
  const kneeFlexEvidenceDeg =
    fromLm?.kneeFlexEvidenceDeg ??
    (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : undefined);
  const kneeHipAntCm =
    fromLm && isFinite(fromLm.kneeHipAntCm) ? fromLm.kneeHipAntCm : parsed.kneeHipAntCm;

  const mildAnteriorTiltDeg = resolveMildAnteriorTiltDeg(input.clientSex);

  // Use the adaptive swayback scoring (Improvement 2)
  const sbBase = scoreSwaybackAdaptive(
    hipFwdCm,
    torsoOffsetCm,
    kneeFlex,
    pelvicTilt,
    rules,
    kneeFlexEvidenceDeg,
    kneeHipAntCm,
    pelvicTiltDegMin,
    mildAnteriorTiltDeg
  );
  const aptBase = scoreApt(
    hipFwdCm,
    torsoOffsetCm,
    kneeHyperext,
    pelvicTilt,
    rules,
    input.clientSex,
    kneeHipAntCm
  );

  let sbPts = sbBase.pts;
  let aptPts = aptBase.pts;
  const sbEvidence: string[] = [...sbBase.evidence];
  const aptEvidence: string[] = [...aptBase.evidence];
  const sbSignals: string[] = [...sbBase.activeSignals];
  const aptSignals: string[] = [...aptBase.activeSignals];

  // --- IMPROVEMENT 3: Bidirectional Forward Head Posture ---
  // When FHP is present AND pelvic tilt is anterior (≥ mild threshold),
  // interpret FHP as a compensatory lordotic APT indicator rather than Swayback.
  const fhpMin = rules.fhpSwaybackDegMin ?? 10;
  const pelvicTiltIsMildlyAnterior =
    isFinite(pelvicTilt) && pelvicTilt >= mildAnteriorTiltDeg;
  const pelvicTiltIsFullyAnterior =
    isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin;

  if (isFinite(avgCvAngle) && avgCvAngle >= fhpMin) {
    if (pelvicTiltIsMildlyAnterior) {
      // FHP + mildly anterior pelvic tilt (≥ 3°) → APT indicator (lordotic compensation)
      aptPts += 1;
      aptSignals.push('vpr.apt.forwardHeadPostureWithAnteriorTilt');
      aptEvidence.push(
        `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°) with anterior pelvic tilt (~${pelvicTilt.toFixed(1)}°) → lordotic APT compensation, not swayback`
      );
    } else {
      // FHP without anterior pelvic tilt → Swayback indicator (original behavior)
      sbPts += 1;
      sbSignals.push('vpr.sb.forwardHeadPosture');
      sbEvidence.push(
        `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°; typical swayback presentation)`
      );
    }
  } else if (
    isFinite(avgCvAngle) &&
    avgCvAngle < fhpMin &&
    pelvicTiltIsFullyAnterior
  ) {
    aptPts += 1;
    aptSignals.push('vpr.apt.neutralHeadPosture');
    aptEvidence.push(
      `[+1] Neutral/mild head posture (CV ~${avgCvAngle.toFixed(1)}° < ${fhpMin}°) with anterior pelvic tilt (~${pelvicTilt.toFixed(1)}°); supports APT presentation`
    );
  }

  // --- Tibial Inclination (unchanged from parent) ---
  const tibPostMin = rules.tibialPostThresholdDeg ?? 3;
  const tibAntMin = rules.tibialAntThresholdDeg ?? 3;
  if (isFinite(tibialInclination) && tibialInclination <= -tibPostMin) {
    sbPts += 1;
    sbSignals.push('vpr.sb.posteriorTibialInclination');
    sbEvidence.push(
      `[+1] Posterior tibial inclination (~${tibialInclination.toFixed(1)}° ≤ −${tibPostMin}°; knee flexion compensation pattern)`
    );
  } else if (isFinite(tibialInclination) && tibialInclination >= tibAntMin) {
    aptPts += 1;
    aptSignals.push('vpr.apt.anteriorTibialInclination');
    aptEvidence.push(
      `[+1] Anterior tibial inclination (~${tibialInclination.toFixed(1)}° ≥ ${tibAntMin}°; hyperextension compensation pattern)`
    );
  }

  // --- Practitioner visual observations (unchanged from parent) ---
  const obs = input.practitionerObservations;
  let toggledCount = 0;

  if (obs?.poochBelly) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.poochBelly');
    sbEvidence.push('[+1] Practitioner observation: pooch belly (swayback indicator)');
  }
  if (obs?.flatGlutes) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.flatGlutes');
    sbEvidence.push('[+1] Practitioner observation: flat/absent glutes (swayback indicator)');
  }
  if (obs?.ribFlare) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.ribFlare');
    aptEvidence.push('[+1] Practitioner observation: rib flare (APT indicator)');
  }
  if (obs?.flatCervicalCurve) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.flatCervicalCurve');
    aptEvidence.push(
      '[+1] Practitioner observation: flat/reduced cervical curve (APT indicator)'
    );
  }

  const dynamicMax = MAX_AUTO_POINTS + toggledCount;

  return {
    sb: {
      pts: Math.min(dynamicMax, sbPts),
      evidence: sbEvidence,
      activeSignals: sbSignals,
    },
    apt: {
      pts: Math.min(dynamicMax, aptPts),
      evidence: aptEvidence,
      activeSignals: aptSignals,
    },
    dynamicMax,
    parsed,
    fromLm,
    rules,
  };
}

export const adaptiveThresholdVprOverrideV2Strategy: PostureOrientationClassificationStrategy = {
  id: STRATEGY_ID,
  displayName: STRATEGY_NAME,
  maxClassificationPoints: MAX_AUTO_POINTS,

  classify(
    input: PostureClassificationStrategyInput,
    context: PostureClassificationStrategyContext
  ): PostureClassificationStrategyResult | null {
    const rulesBundle = getEvaluationRules() as any;
    const rules = rulesBundle.posture.thresholds;
    const veto = readVetoThresholds(rules);

    const augmented = augmentScores(input, context);
    if (!augmented) return null;

    const { sb, apt, dynamicMax, parsed, fromLm } = augmented;

    // Extract resolved lateral parameters
    const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
    const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
    const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
    const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
    const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
    const kneeFlexDeg =
      fromLm?.kneeFlexEvidenceDeg ??
      (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : NaN);

    let kneeHyperextDeg = NaN;
    if (fromLm && input.sagittal?.landmarks?.length) {
      const s = input.sagittal;
      const side = s.sideView;
      const idxLR = (l: number, r: number) => (side === 'Left' ? l : r);
      const H = s.landmarks[idxLR(IDX.L_HIP, IDX.R_HIP)];
      const K = s.landmarks[idxLR(IDX.L_KNEE, IDX.R_KNEE)];
      const A = s.landmarks[idxLR(IDX.L_ANK, IDX.R_ANK)];
      if (H && K && A && Math.abs(A.y - H.y) > 10) {
        const kneeRawAngle = angleAtLandmark(H, K, A);
        const kneeDeg = isFinite(kneeRawAngle) ? Math.abs(180 - kneeRawAngle) : NaN;
        kneeHyperextDeg = kneeDeg;
      }
    }
    if (!isFinite(kneeHyperextDeg)) {
      kneeHyperextDeg = parsed.maxKneeHyperextDeg;
    }

    const isPinned =
      !!context.asisPsisLine ||
      (!!input.sagittal?.custom?.ASIS && !!input.sagittal?.custom?.PSIS) ||
      (isFinite(pelvicTilt) && !parsed.isPelvicTiltEstimated);

    // We will trace which vetoes and overrides have fired
    let isSwaybackExcluded = false;
    let isAptExcluded = false;
    let forceStacked = false;
    const overrideAndVetoEvidence: string[] = [];

    // 1. SoP Vetoes (Tier 1/2) — unchanged from parent strategy
    const sop = input.sopMetrics;
    if (sop) {
      const hipExtensionFullBilateral = bothAtLeast(
        sop.hipExtensionLeft,
        sop.hipExtensionRight,
        veto.fullHipExtensionDeg
      );
      const obersNeutralBilateral = bothAtMost(sop.oberLeft, sop.oberRight, veto.oberNeutralDeg);
      if (hipExtensionFullBilateral || obersNeutralBilateral) {
        isSwaybackExcluded = true;
        isAptExcluded = true;
        forceStacked = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] SoP: full hip extension / neutral Ober's bilaterally → no SB/APT compensation → Stacked`
        );
      }

      const irTotal = sumDefined(sop.femoralIrLeft, sop.femoralIrRight);
      const erTotal = sumDefined(sop.femoralErLeft, sop.femoralErRight);
      if (irTotal !== null && erTotal !== null) {
        if (irTotal > erTotal) {
          isSwaybackExcluded = true;
          overrideAndVetoEvidence.push(
            `[VETO] SoP: femoral IR (${irTotal.toFixed(0)}°) > ER (${erTotal.toFixed(0)}°) → excludes Swayback (anterior orientation confirmed)`
          );
        } else if (erTotal > irTotal) {
          isAptExcluded = true;
          overrideAndVetoEvidence.push(
            `[VETO] SoP: femoral ER (${erTotal.toFixed(0)}°) > IR (${irTotal.toFixed(0)}°) → excludes APT (posterior lower compression confirmed)`
          );
        }
      }

      const avgSlr = avgDefined(sop.slrLeft, sop.slrRight);
      if (avgSlr !== null && avgSlr > veto.slrSwaybackVetoMaxDeg) {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push(
          `[VETO] SoP: SLR (${avgSlr.toFixed(0)}°) > ${veto.slrSwaybackVetoMaxDeg}° → excludes Swayback`
        );
      }
    }

    // 2. Image Vetoes & Overrides (Tier 1/3)

    // Extreme Knee Flexion Override — suppressed when pelvic tilt indicates anterior orientation
    // The mild anterior tilt guard (≥3°) prevents false APT exclusion when the pelvis is already
    // showing anterior orientation (e.g. Matt: knee 19°, pelvic tilt 4.0° → guard fires → APT preserved).
    let extremeKneeFlexOverride = false;
    const mildAnteriorTiltDeg = resolveMildAnteriorTiltDeg(input.clientSex);
    const pelvicTiltIsAnteriorForKneeGuard =
      isFinite(pelvicTilt) && pelvicTilt >= mildAnteriorTiltDeg;
    if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin && !pelvicTiltIsAnteriorForKneeGuard) {
      isAptExcluded = true;
      extremeKneeFlexOverride = true;
      overrideAndVetoEvidence.push(
        `[OVERRIDE] Image: extreme knee flexion (~${kneeFlexDeg.toFixed(0)}° > ${veto.kneeFlexionExtremeDegMin}°) → excludes APT and boosts Swayback`
      );
    } else if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin && pelvicTiltIsAnteriorForKneeGuard) {
      overrideAndVetoEvidence.push(
        `[INFO] Image: extreme knee flexion (~${kneeFlexDeg.toFixed(0)}°) present but APT exclusion suppressed: pelvic tilt is anterior (~${pelvicTilt.toFixed(1)}° ≥ ${mildAnteriorTiltDeg}°)`
      );
    }

    // Extreme Knee Hyperextension Override / R5 Veto (unchanged)
    let extremeKneeHyperextOverride = false;
    if (kneeHyperext) {
      if (isFinite(kneeHyperextDeg) && kneeHyperextDeg >= veto.kneeHyperextensionExtremeDegMin) {
        isSwaybackExcluded = true;
        extremeKneeHyperextOverride = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] Image: extreme knee hyperextension (~${kneeHyperextDeg.toFixed(0)}° ≥ ${veto.kneeHyperextensionExtremeDegMin}°) → excludes Swayback and boosts APT`
        );
      } else {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push('[VETO] Image: knee hyperextension → excludes Swayback');
      }
    }

    // Pelvic Tilt Override — extreme threshold only (≥15° with pinned ASIS/PSIS)
    let pelvicTiltOverride = false;
    if (isFinite(pelvicTilt)) {
      if (isPinned && pelvicTilt >= veto.pelvicTiltVetoDegMin) {
        isSwaybackExcluded = true;
        pelvicTiltOverride = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] Image: pelvic tilt ≥ ${veto.pelvicTiltVetoDegMin}° with pinned ASIS/PSIS (~${pelvicTilt.toFixed(1)}°) → excludes Swayback and boosts APT`
        );
      }
    }

    // --- IMPROVEMENT 1: Softened C-curve veto ---
    // Hard exclusion only at -5.0 cm (was -2.5 cm).
    // Range -2.5 cm to -5.0 cm applies a weighted penalty instead of exclusion.
    if (isFinite(torsoOffsetCm)) {
      if (torsoOffsetCm > rules.strictLean) {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push(
          `[VETO] Image: shoulders forward of hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm > ${rules.strictLean} cm) → excludes Swayback`
        );
      } else if (torsoOffsetCm < HARD_C_CURVE_VETO_CM) {
        // Severe C-curve → hard APT exclusion (shoulders > 5 cm behind hips)
        isAptExcluded = true;
        overrideAndVetoEvidence.push(
          `[VETO] Image: severe "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm < ${HARD_C_CURVE_VETO_CM} cm) → excludes APT`
        );
      } else if (torsoOffsetCm < SOFT_C_CURVE_PENALTY_CM) {
        // Mild C-curve → penalty (not exclusion). Deduct points from APT score.
        overrideAndVetoEvidence.push(
          `[PENALTY] Image: mild "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm, between ${HARD_C_CURVE_VETO_CM} and ${SOFT_C_CURVE_PENALTY_CM} cm) → −${C_CURVE_PENALTY_POINTS} APT points (not excluded)`
        );
      }
    }

    // Apply initial points
    let finalScoreSwayback = sb.pts;
    let finalScoreApt = apt.pts;

    // Apply the soft C-curve penalty (Improvement 1)
    if (
      isFinite(torsoOffsetCm) &&
      torsoOffsetCm < SOFT_C_CURVE_PENALTY_CM &&
      torsoOffsetCm >= HARD_C_CURVE_VETO_CM
    ) {
      finalScoreApt = Math.max(0, finalScoreApt - C_CURVE_PENALTY_POINTS);
    }

    // Apply override boosts
    if (extremeKneeFlexOverride) {
      finalScoreSwayback += veto.overridePointBoost;
    }
    if (extremeKneeHyperextOverride) {
      finalScoreApt += veto.overridePointBoost;
    }
    if (pelvicTiltOverride) {
      finalScoreApt += veto.overridePointBoost;
    }

    // Exclude scores if vetoed/overridden
    if (isSwaybackExcluded) {
      finalScoreSwayback = 0;
    }
    if (isAptExcluded) {
      finalScoreApt = 0;
    }

    // Final decision mapping
    let orientation: PostureOrientationLabel = 'Stacked';
    let matched = 0;
    let winnerEvidence: string[] = [];
    let decisionPath = '';

    if (forceStacked || (finalScoreSwayback === 0 && finalScoreApt === 0)) {
      orientation = 'Stacked';
      matched = 0;
      winnerEvidence = forceStacked
        ? []
        : ['No surviving swayback vs anterior-orientation points after exclusions.'];
      decisionPath = forceStacked ? 'veto.forceStacked' : 'noSurvivingPoints.stacked';
    } else if (finalScoreSwayback > finalScoreApt) {
      orientation = 'Swayback';
      matched = finalScoreSwayback;
      winnerEvidence = sb.evidence;
      decisionPath = 'sbSurvives';
    } else if (finalScoreApt > finalScoreSwayback) {
      orientation = 'Anterior Pelvic Tilt';
      matched = finalScoreApt;
      winnerEvidence = apt.evidence;
      decisionPath = 'aptSurvives';
    } else {
      // Tie-breaker
      if (hipFwdCm >= rules.hipForwardCmMin && finalScoreSwayback > 0) {
        orientation = 'Swayback';
        matched = finalScoreSwayback;
        winnerEvidence = [...sb.evidence, 'Tie-break: large hip–ankle offset favors swayback shift.'];
        decisionPath = 'tieBreak.hipFwdFavorsSwayback';
      } else {
        orientation = 'Anterior Pelvic Tilt';
        matched = finalScoreApt;
        winnerEvidence = [...apt.evidence, 'Tie-break: hip stack not dominant → anterior orientation.'];
        decisionPath = 'tieBreak.hipStackFavorsApt';
      }
    }

    const evidence = [...overrideAndVetoEvidence, ...winnerEvidence];

    const confidencePct =
      orientation === 'Stacked'
        ? Math.min(
            100,
            Math.round(
              (1 - Math.max(finalScoreSwayback, finalScoreApt) / Math.max(1, dynamicMax * 2)) *
                100
            )
          )
        : Math.min(100, Math.round((matched / dynamicMax) * 100));

    logPostureClassificationDebug(DEBUG_LOG_PREFIX, 'adaptiveThresholdVprOverride classification', {
      rawScoreSwayback: sb.pts,
      rawScoreApt: apt.pts,
      finalScoreSwayback,
      finalScoreApt,
      isSwaybackExcluded,
      isAptExcluded,
      forceStacked,
      orientation,
      decisionPath,
      hasSopMetrics: !!input.sopMetrics,
      vetoEvidence: overrideAndVetoEvidence,
      adaptiveThresholds: {
        mildAnteriorTiltDeg: resolveMildAnteriorTiltDeg(input.clientSex),
        hardCCurveVetoCm: HARD_C_CURVE_VETO_CM,
        softCCurvePenaltyCm: SOFT_C_CURVE_PENALTY_CM,
      },
    });

    return {
      orientation,
      confidencePct,
      matchedPoints: matched,
      maxPoints: dynamicMax,
      strategyId: STRATEGY_ID,
      strategyDisplayName: STRATEGY_NAME,
      evidence,
    };
  },
};
